// src/api/features/modules/queries.ts
import {
  useQuery,
  useQueryClient,
  type QueryKey,
  keepPreviousData,
} from "@tanstack/react-query";

import { listModules, getModule } from "./api";
import type { ModuleDTO } from "./types";
import { ModulesKeys } from "./types";

/* =========================================
 * MODULES — Read Hooks (Queries Only)
 * ========================================= */

/** 🔹 List modules with pagination + filters
 *  NOTE: Backend returns only active / non-deleted modules.
 */
export function useModules(params: ModuleDTO.ListParams = {}) {
  return useQuery({
    queryKey: ModulesKeys.list(params),
    queryFn: () => listModules(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/** 🔹 Get a single module */
export function useModule(id: number | null | undefined) {
  const enabled = typeof id === "number";

  const key: QueryKey = enabled
    ? ModulesKeys.detail(id as number)
    : (["modules", "detail", "disabled"] as QueryKey);

  return useQuery({
    queryKey: key,
    queryFn: () => getModule(id as number),
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/* =========================================
 * Prefetch Helpers (Optional)
 * ========================================= */

export function prefetchModules(
  qc: ReturnType<typeof useQueryClient>,
  params: ModuleDTO.ListParams = {}
) {
  return qc.prefetchQuery({
    queryKey: ModulesKeys.list(params),
    queryFn: () => listModules(params),
    staleTime: 30_000,
  });
}

export function prefetchModule(
  qc: ReturnType<typeof useQueryClient>,
  id: number
) {
  return qc.prefetchQuery({
    queryKey: ModulesKeys.detail(id),
    queryFn: () => getModule(id),
    staleTime: 60_000,
  });
}
